/**
 * TEMPORARY in-app feedback tool — screen capture.
 *
 * Grabs a picture of what is on screen when a note is logged, so a bug report
 * carries the actual view rather than just a description — and, crucially, the
 * menu or sheet the user had open at the time. We compose two layers:
 *
 *  1. The MapLibre chart canvas (WebGL). On desktop the map keeps a preserved
 *     drawing buffer so we can read it back any time; on mobile the buffer is
 *     not preserved (to save GPU memory), so we grab a one-shot capture inside a
 *     render pass instead.
 *  2. The DOM + glass overlays sitting on top of the chart (the HUD, chips,
 *     sheets, menus, popovers, markers), rendered with html2canvas over the
 *     whole body but with the chart canvas itself skipped, so it comes back as a
 *     transparent layer we can lay over the chart.
 *
 * Drawing the DOM layer on top of the chart layer gives a shot that shows the
 * open menu exactly where it was. Earlier, mobile grabbed the bare chart only,
 * so any open sheet/menu was missing from the picture — that is what this fixes.
 * The glass blur (backdrop-filter) cannot be reproduced exactly, so those panels
 * come out a touch flatter than on screen, but everything is there.
 *
 * ORDER MATTERS. On mobile the WebGL drawing buffer is not preserved, so the
 * chart pixels can only be read back inside the same frame MapLibre paints them,
 * before the browser composites the canvas and clears the buffer. html2canvas
 * forces a full-page layout and paint while it renders, which triggers exactly
 * that composite. Running the two together (as an earlier version did) let the
 * html2canvas composite land first and clear the buffer, so the chart read came
 * back black. We now grab the chart pixels first and fully, then render the
 * overlay, so nothing clears the buffer between MapLibre's paint and our read.
 *
 * ANDROID BLACK-READ. Beyond staleness, some Android WebViews return an all-black
 * image from canvas.toDataURL() even when we read INSIDE a live render pass — the
 * on-screen map looks fine, but the toDataURL path comes back black. Reading the
 * same framebuffer directly with gl.readPixels returns the real frame, so we try
 * that first and only fall back to toDataURL if the GL read is unavailable or
 * itself comes back all-black. See grabMapPixels / readMapPixelsViaGL.
 *
 * We record which layers made it in, so a note never lies about what it shows:
 *  - 'html2canvas' — the DOM overlays were captured (with or without the chart
 *    underneath), so any open menu is present.
 *  - 'map-canvas'  — only the bare chart could be grabbed (html2canvas failed),
 *    so the overlays are missing.
 *
 * BOLT-ON: part of src/feedback; delete the folder to strip the whole tool.
 */
import { getActiveMap } from '@/map/mapHandle';

export type ShotMethod = 'html2canvas' | 'map-canvas';

export interface Screenshot {
  dataUrl: string;
  method: ShotMethod;
}

/** Cap the longest edge so the PNG stays a sensible size for the endpoint. */
const MAX_EDGE = 1600;

/** How many pixels we sample when deciding whether a readback is all-black. */
const BLACK_SAMPLE = 4096;

/**
 * Which read path produced the chart pixels, plus a few cheap breadcrumbs, so a
 * future black shot is not blind. Logged with a `[capture]` prefix AND carried
 * into the bug report itself via lastCaptureDiag -> captureContext. The comment
 * here used to claim the collector snapshots the console; it never did, which
 * is why build 255's black shot taught us nothing: the diag existed and died in
 * a console no phone can show. Diagnostics for field bugs live in the report or
 * they do not exist.
 */
export interface CaptureDiag {
  /** Was the live MapLibre instance reachable (else we did a plain idle read)? */
  hadMap: boolean;
  /** The chart canvas backing-store size at read time. */
  canvasW: number;
  canvasH: number;
  /** Which read actually produced the returned pixels. */
  path: 'readPixels' | 'drawImage' | 'toDataURL' | 'none';
  /** Non-black pixels found in the sampled slice of the readPixels buffer. */
  nonBlackSampled: number;
  /** How many pixels we sampled (0 when readPixels never ran). */
  sampled: number;
  /**
   * Fully-opaque pixels found in a sample of the html2canvas OVERLAY layer.
   * The overlay is meant to be transparent glass over the chart; an overlay
   * that samples ~all-opaque is a slab that will blot the chart out no matter
   * how well the GL read went — which is exactly what build 259 shipped: a
   * perfect readPixels chart buried under an opaque #000 body background.
   * Optional because a chart-only capture has no overlay to sample.
   */
  overlayOpaque?: number;
  overlaySampled?: number;
}

/**
 * The scale factor that keeps the longest edge within `maxEdge`, never scaling
 * up (cap at 1). Pure so it can be unit-tested without a DOM. A zero or negative
 * edge is treated as 1 so we never divide by zero or return a silly factor.
 */
export function scaleForLongestEdge(
  width: number,
  height: number,
  maxEdge: number = MAX_EDGE,
): number {
  const longest = Math.max(width, height, 1);
  return Math.min(1, maxEdge / longest);
}

/**
 * Which layers we managed to grab decide the recorded method. If the overlay
 * rendered we always report 'html2canvas' (the chart may or may not be under
 * it); if only the bare chart came back it is 'map-canvas'; nothing at all is
 * null. Pure and exported so the decision can be unit-tested on its own.
 */
export function chooseMethod(hasOverlay: boolean, hasMap: boolean): ShotMethod | null {
  if (hasOverlay) return 'html2canvas';
  if (hasMap) return 'map-canvas';
  return null;
}

/**
 * Count non-black pixels among the first `sample` pixels of an RGBA buffer. A
 * pixel counts as black when its R, G, and B are all zero (alpha is ignored, as
 * a cleared buffer is transparent-black and a painted chart is not). Pure and
 * exported so the all-black check can be unit-tested without a GL context.
 */
export function countNonBlack(pixels: Uint8Array, sample: number): number {
  const px = Math.min(sample, Math.floor(pixels.length / 4));
  let n = 0;
  for (let i = 0; i < px; i++) {
    const o = i * 4;
    if (pixels[o] !== 0 || pixels[o + 1] !== 0 || pixels[o + 2] !== 0) n++;
  }
  return n;
}

/**
 * True for elements html2canvas should skip when we render the DOM overlay
 * layer. We skip only the WebGL chart canvas itself: it reads back blank on
 * mobile (no preserved buffer) and we composite the real chart pixels under the
 * overlay anyway. Everything else — sheets, menus, the HUD, and the map's own
 * DOM markers (ownship, AIS) — stays in, so an open menu is captured.
 *
 * Pure and exported so the behaviour can be unit-tested without a real DOM.
 */
export function shouldIgnoreForOverlay(el: Element): boolean {
  return el.classList?.contains('maplibregl-canvas') ?? false;
}

/** The scale factor that keeps the longest screen edge within MAX_EDGE. */
function overlayScale(): number {
  return scaleForLongestEdge(window.innerWidth, window.innerHeight, MAX_EDGE);
}

/** The live chart canvas, or null if the map is not mounted. */
function mapCanvas(): HTMLCanvasElement | null {
  return document.querySelector<HTMLCanvasElement>('.maplibregl-canvas');
}

/** Read the bare chart canvas back as a data URL (needs a readable buffer). */
function readMapCanvas(): string | null {
  const canvas = mapCanvas();
  if (!canvas) return null;
  try {
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Read the chart straight out of the WebGL framebuffer with gl.readPixels, then
 * repaint it (flipped) into a 2D canvas so we can PNG it.
 *
 * WHY this and not toDataURL: on some Android WebViews canvas.toDataURL() returns
 * an all-black image even when we read inside a live render pass, but readPixels
 * on the very same context returns the real frame. So on those devices this is
 * the path that actually works, and toDataURL is only a fallback.
 *
 * We deliberately call getContext WITH NO ATTRIBUTES, so we get back the SAME
 * context MapLibre already created rather than a fresh one with mismatched attrs
 * (which returns null). If we cannot get a context, or the read comes back
 * all-black, we return null so the caller can fall back to toDataURL. Writes the
 * canvas size and non-black count into `diag` either way.
 */
function readMapPixelsViaGL(diag: CaptureDiag): string | null {
  const canvas = mapCanvas();
  if (!canvas) return null;

  const gl = (canvas.getContext('webgl2') || canvas.getContext('webgl')) as
    | WebGLRenderingContext
    | WebGL2RenderingContext
    | null;
  if (!gl) return null;

  const w = canvas.width;
  const h = canvas.height;
  if (w <= 0 || h <= 0) return null;

  const pixels = new Uint8Array(w * h * 4);
  try {
    gl.readPixels(0, 0, w, h, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  } catch {
    return null;
  }

  diag.sampled = Math.min(BLACK_SAMPLE, w * h);
  diag.nonBlackSampled = countNonBlack(pixels, BLACK_SAMPLE);
  // An all-black readback is exactly the bug we are working around; let the
  // caller fall back to toDataURL rather than saving a black frame from here.
  if (diag.nonBlackSampled === 0) return null;

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const g = out.getContext('2d');
  if (!g) return null;

  // readPixels is bottom-up: row 0 is the BOTTOM of the image, but ImageData is
  // top-down, so we copy the rows in reverse to flip the Y axis as we go.
  const img = g.createImageData(w, h);
  const rowBytes = w * 4;
  for (let y = 0; y < h; y++) {
    const src = y * rowBytes;
    const dst = (h - 1 - y) * rowBytes;
    img.data.set(pixels.subarray(src, src + rowBytes), dst);
  }
  g.putImageData(img, 0, 0);

  try {
    return out.toDataURL('image/png');
  } catch {
    return null;
  }
}

let lastDiag: CaptureDiag | null = null;

/**
 * The diagnostics of the most recent capture, for the bug report context. A
 * black screenshot with path/none and zero non-black pixels is a GL readback
 * failure; the same shot with path/toDataURL and no sampling means the fallback
 * lied. Either way the report now says which, instead of us guessing from the
 * colour of the void.
 */
export function lastCaptureDiag(): CaptureDiag | null {
  return lastDiag ? { ...lastDiag } : null;
}

/** Console breadcrumb so the NEXT black shot is not blind. Never throws. */
function logCaptureDiag(d: CaptureDiag): void {
  lastDiag = { ...d };
  try {
    // eslint-disable-next-line no-console
    console.log(
      `[capture] map=${d.hadMap} canvas=${d.canvasW}x${d.canvasH} ` +
        `path=${d.path} nonBlack=${d.nonBlackSampled}/${d.sampled}`,
    );
  } catch {
    /* logging must never break the capture */
  }
}

/**
 * Third read path: paint the live GL canvas onto a 2D canvas with drawImage and
 * PNG that. This goes through the compositor's copy of the frame rather than
 * the drawing buffer, and on several Android WebViews it works where BOTH
 * gl.readPixels and canvas.toDataURL return black. Sampled for blackness like
 * the GL path, because a black copy of a black buffer proves nothing.
 */
function readMapViaDrawImage(diag: CaptureDiag): string | null {
  const canvas = mapCanvas();
  if (!canvas) return null;
  const w = canvas.width;
  const h = canvas.height;
  if (w <= 0 || h <= 0) return null;
  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const g = out.getContext('2d');
  if (!g) return null;
  try {
    g.drawImage(canvas, 0, 0);
    const sampleH = Math.max(1, Math.min(h, Math.ceil(BLACK_SAMPLE / w)));
    const data = g.getImageData(0, 0, w, sampleH).data;
    const nonBlack = countNonBlack(new Uint8Array(data.buffer, data.byteOffset, data.byteLength), BLACK_SAMPLE);
    if (diag.sampled === 0) {
      diag.sampled = Math.min(BLACK_SAMPLE, w * sampleH);
      diag.nonBlackSampled = nonBlack;
    }
    if (nonBlack === 0) return null;
    return out.toDataURL('image/png');
  } catch {
    return null;
  }
}

/**
 * Read the chart pixels once. Three paths, each black-checked before we trust
 * it, in the order they are likely to survive an Android WebView:
 *
 *   1. gl.readPixels — the drawing buffer, checked for blackness already.
 *   2. drawImage onto a 2D canvas — the compositor's copy, checked above.
 *   3. canvas.toDataURL — last resort, and the one path we cannot cheaply
 *      check. It used to sit second with no check at all, which is exactly how
 *      build 255 saved a coal-black chart while reporting success: readPixels
 *      read zeros and was rightly discarded, toDataURL encoded the same zeros
 *      into a perfectly valid PNG of nothing, and nobody asked it any questions.
 *
 * Meant to be called INSIDE MapLibre's render pass, while the freshly painted
 * frame is still in the buffer. Records the path taken into `diag`.
 */
function readChartNow(diag: CaptureDiag): string | null {
  const canvas = mapCanvas();
  diag.canvasW = canvas?.width ?? 0;
  diag.canvasH = canvas?.height ?? 0;

  const viaGL = readMapPixelsViaGL(diag);
  if (viaGL) {
    diag.path = 'readPixels';
    return viaGL;
  }

  const viaDraw = readMapViaDrawImage(diag);
  if (viaDraw) {
    diag.path = 'drawImage';
    return viaDraw;
  }

  const viaData = readMapCanvas();
  if (viaData) {
    diag.path = 'toDataURL';
    return viaData;
  }

  diag.path = 'none';
  return null;
}

/**
 * Grab the chart pixels as a data URL.
 *
 * MapLibre only repaints on demand, it does not continuously redraw an idle map.
 * So reading the WebGL canvas back while the map is sitting still can return a
 * stale or cleared (black) buffer on some Android GPUs, EVEN with
 * preserveDrawingBuffer:true. The on-screen map still looks fine because that is
 * a composited layer; the buffer we read back is the thing that goes stale.
 *
 * The robust fix is two-part: force a fresh frame and read INSIDE that render
 * pass (so the pixels are live), and read them with gl.readPixels rather than
 * toDataURL (so an Android WebView that blacks out toDataURL still gives us the
 * real frame). We force the repaint whenever we can reach the map instance, on
 * every device — device detection is not reliable enough to gate it on, and the
 * extra repaint is cheap and harmless on desktop. If we cannot reach the map at
 * all we read whatever is currently in the buffer, still preferring readPixels.
 */
function grabMapPixels(): Promise<string | null> {
  const map = getActiveMap();
  const diag: CaptureDiag = {
    hadMap: !!map,
    canvasW: 0,
    canvasH: 0,
    path: 'none',
    nonBlackSampled: 0,
    sampled: 0,
  };

  const finish = (url: string | null): string | null => {
    logCaptureDiag(diag);
    return url;
  };

  // No reachable map: read whatever is in the buffer right now (may be stale).
  if (!map) return Promise.resolve(finish(readChartNow(diag)));

  return new Promise((resolve) => {
    let settled = false;
    const grab = () => {
      if (settled) return;
      settled = true;
      // Read synchronously inside the render handler, while the freshly painted
      // frame is still in the buffer.
      resolve(finish(readChartNow(diag)));
    };
    // Read on the next painted frame, then ask MapLibre to paint one right now.
    map.once('render', grab);
    map.triggerRepaint();
    // Safety net: never hang the capture. If 'render' somehow never fires we read
    // anyway after a short wait. This read may be stale, but the feedback flow
    // must not block on it.
    window.setTimeout(grab, 250);
  });
}

/**
 * Elements whose own opaque background paints would blot the chart out of the
 * overlay layer. `backgroundColor: null` only keeps html2canvas's CANVAS
 * transparent; the elements' background styles still render, and our app sets
 * `html, body, #root { background: #000 }`. Skipping the chart canvas just
 * exposed that black slab behind it, so the overlay came back opaque black
 * everywhere but the chips — and compositing it OVER a perfectly good chart
 * read produced build 259's black screenshot (report fb-msaevlca-0:
 * path=readPixels nonBlack=4096/4096, picture still coal). The chart pixels
 * come in as their own layer underneath, so in the overlay clone these
 * backdrops have no job to do; we clear them.
 */
export const OVERLAY_CLEARED_SELECTORS = ['html', 'body', '#root', '.maplibregl-map'];

/**
 * Clear the opaque backdrop backgrounds in the html2canvas CLONE document (the
 * live page is never touched). Pure over the doc handed in, so it can be
 * unit-tested with a stub.
 */
export function clearOpaqueBackdrops(doc: {
  querySelectorAll(sel: string): ArrayLike<{ style: { background: string } }>;
}): void {
  for (const sel of OVERLAY_CLEARED_SELECTORS) {
    const els = doc.querySelectorAll(sel);
    for (let i = 0; i < els.length; i++) els[i].style.background = 'transparent';
  }
}

/**
 * Count fully-opaque pixels in a sample band of the rendered overlay, for the
 * capture diag. A healthy overlay (glass chips on transparency) samples mostly
 * non-opaque; a slab samples at ~100% and names itself in the report.
 */
function sampleOverlayOpacity(overlay: HTMLCanvasElement, diag: CaptureDiag): void {
  try {
    const g = overlay.getContext('2d');
    if (!g || overlay.width <= 0 || overlay.height <= 0) return;
    // Sample a band from the vertical middle: the top of the screen is dense
    // with chips (legitimately opaque), the middle is where the chart shows.
    const sampleH = Math.max(1, Math.min(overlay.height, Math.ceil(BLACK_SAMPLE / overlay.width)));
    const y = Math.max(0, Math.floor(overlay.height / 2) - Math.floor(sampleH / 2));
    const data = g.getImageData(0, y, overlay.width, sampleH).data;
    const px = Math.min(BLACK_SAMPLE, Math.floor(data.length / 4));
    let opaque = 0;
    for (let i = 0; i < px; i++) if (data[i * 4 + 3] === 255) opaque++;
    diag.overlayOpaque = opaque;
    diag.overlaySampled = px;
  } catch {
    /* diagnostics never break the capture */
  }
}

/** Render the DOM + glass overlays (everything but the chart) to a canvas. */
async function overlayLayer(scale: number): Promise<HTMLCanvasElement | null> {
  try {
    const { default: html2canvas } = await import('html2canvas');
    return await html2canvas(document.body, {
      useCORS: true,
      logging: false,
      scale,
      // Transparent background so the chart layer shows through underneath.
      backgroundColor: null,
      ignoreElements: shouldIgnoreForOverlay,
      // The app's own full-viewport #000 backgrounds must not ride along, or
      // the "transparent" overlay is an opaque black slab. See the note on
      // OVERLAY_CLEARED_SELECTORS.
      onclone: (doc: Document) => clearOpaqueBackdrops(doc),
    });
  } catch {
    return null;
  }
}

/** Load a data URL into an Image so it can be drawn onto a 2D canvas. */
function loadImage(dataUrl: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = dataUrl;
  });
}

/**
 * Capture the current screen. Resolves to null if nothing could be grabbed (the
 * note is still worth saving without a picture). Call this BEFORE opening the
 * modal, so the shot shows the screen the user was looking at, not the dialog.
 */
export async function captureScreenshot(): Promise<Screenshot | null> {
  const scale = overlayScale();

  // Grab the chart FIRST and fully, then render the overlay. See the header note:
  // running html2canvas alongside the chart read composites and clears the (not
  // preserved, on mobile) WebGL buffer before we read it, which is what made the
  // chart save black. Sequencing keeps the read inside MapLibre's own frame.
  const mapUrl = await grabMapPixels();
  const overlay = await overlayLayer(scale);
  // Stamp the overlay's opacity into the diag grabMapPixels just recorded, so
  // the report can tell "chart read failed" from "overlay buried the chart".
  if (overlay && lastDiag) sampleOverlayOpacity(overlay, lastDiag);

  const method = chooseMethod(!!overlay, !!mapUrl);
  if (method === null) return null;

  // No overlay layer means html2canvas failed. Fall back to the bare chart if we
  // have it, so at least the view is captured (overlays lost, recorded honestly).
  if (!overlay) {
    // method is 'map-canvas' here, and mapUrl is non-null (chooseMethod said so).
    return { dataUrl: mapUrl as string, method };
  }

  const out = document.createElement('canvas');
  out.width = overlay.width;
  out.height = overlay.height;
  const g = out.getContext('2d');
  // Without a 2D context we cannot composite; the overlay alone still carries
  // the open menu, which is the whole point of this capture.
  if (!g) return { dataUrl: overlay.toDataURL('image/png'), method };

  // Chart underneath (stretched to fill the overlay's pixel size), overlays on
  // top. The chart canvas covers the full viewport, so a straight fill lines up.
  // When the chart could not be read at all, fill a neutral paper tone rather
  // than leaving transparency: most PNG viewers render transparency as black,
  // which made a missing chart INDISTINGUISHABLE from a captured black chart,
  // and that ambiguity cost a diagnostic round trip. The diag in the report
  // says the chart is absent; the picture should look absent, not broken.
  let chartDrawn = false;
  if (mapUrl) {
    const img = await loadImage(mapUrl);
    if (img) {
      g.drawImage(img, 0, 0, out.width, out.height);
      chartDrawn = true;
    }
  }
  if (!chartDrawn) {
    g.fillStyle = '#e8e4d8';
    g.fillRect(0, 0, out.width, out.height);
  }
  g.drawImage(overlay, 0, 0);
  return { dataUrl: out.toDataURL('image/png'), method };
}
